import { Suspense, lazy, type ComponentType, type LazyExoticComponent } from "react";
import { Switch, Route, Router, useRoute } from "wouter";
import { useHashLocation } from "wouter/use-hash-location";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/not-found";
import Library from "@/pages/Library";
import { ThemeProvider } from "@/components/ThemeProvider";
import { SyncBootstrap } from "@/components/SyncStatus";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Onboarding } from "@/components/Onboarding";
import { AuthProvider } from "@/lib/auth";

/**
 * Wraps React.lazy() with self-recovery for the classic SPA "stale chunk"
 * scenario: the page was loaded from an old build whose index.html
 * referenced asset hashes that no longer exist after a redeploy. When the
 * dynamic import 404s we force one reload, which re-fetches the current
 * index.html and the current hashes. A query param guards against infinite
 * loops if the failure is something else.
 */
function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err: unknown) {
      const msg = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
      const isChunkLoadError =
        /dynamically imported module|Failed to fetch|importing a module|chunk|ChunkLoadError/i.test(msg);
      if (isChunkLoadError && typeof window !== "undefined") {
        const url = new URL(window.location.href);
        if (!url.searchParams.has("__refreshed")) {
          url.searchParams.set("__refreshed", String(Date.now()));
          window.location.replace(url.toString());
          // Return a never-resolving promise so React shows the Suspense
          // fallback for the brief moment before the navigation happens.
          return new Promise<{ default: T }>(() => undefined);
        }
      }
      throw err;
    }
  });
}

const ChapterPage = lazyWithRetry(() => import("@/pages/ChapterPage"));
const ProgressPage = lazyWithRetry(() => import("@/pages/Progress"));
const DrillPage = lazyWithRetry(() => import("@/pages/Drill"));
const TodayPage = lazyWithRetry(() => import("@/pages/Today"));
const ExamPage = lazyWithRetry(() => import("@/pages/Exam"));
const CoachPage = lazyWithRetry(() => import("@/pages/Coach"));
const PracticePage = lazyWithRetry(() => import("@/pages/Practice"));
const FocusPage = lazyWithRetry(() => import("@/pages/Focus"));

function RouteFallback() {
  return (
    <div className="min-h-dvh flex items-center justify-center bg-background text-muted-foreground text-sm">
      Loading...
    </div>
  );
}

// Plain-DOM skip-to-content. Uses a button + programmatic focus rather than
// an anchor with href="#main" so the hash router never interprets the click
// as a route change.
function SkipToContent() {
  return (
    <button
      type="button"
      onClick={() => {
        const target = document.getElementById("main");
        if (target) {
          target.focus({ preventScroll: false });
        }
      }}
      className="absolute left-2 top-2 z-[100] -translate-y-12 focus:translate-y-0 transition-transform px-3 py-2 rounded-md bg-primary text-primary-foreground text-[13px] font-medium shadow-md focus:outline-none focus:ring-2 focus:ring-primary/40"
      data-testid="skip-to-content"
    >
      Skip to content
    </button>
  );
}

function ChapterRoute() {
  const [, params] = useRoute<{ id: string }>("/chapter/:id");
  const id = params?.id ?? "";
  return <ChapterPage chapterId={id} />;
}

function AppRouter() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <div id="main" tabIndex={-1}>
        <Switch>
          <Route path="/" component={Library} />
          <Route path="/progress" component={ProgressPage} />
          <Route path="/drill" component={DrillPage} />
          <Route path="/today" component={TodayPage} />
          <Route path="/exam" component={ExamPage} />
          <Route path="/coach" component={CoachPage} />
          <Route path="/practice" component={PracticePage} />
          <Route path="/focus" component={FocusPage} />
          <Route path="/chapter/:id" component={ChapterRoute} />
          <Route component={NotFound} />
        </Switch>
      </div>
    </Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ThemeProvider>
            <TooltipProvider>
              <SkipToContent />
              <Toaster />
              <SyncBootstrap />
              <Router hook={useHashLocation}>
                <Onboarding />
                <AppRouter />
              </Router>
            </TooltipProvider>
          </ThemeProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
